import suggestions from '@/components/v2/suggestionsFloatingPane/suggestionContexts';
import useAppScreenResponseInput from '@/hooks/useAppScreenResponseInput';
import extractSuggestionContent from '@/hooks/useSuggestions/extractSuggestionContent';
import getContentEditableCaretPosition from '@/hooks/useSuggestions/getContentEditableCaretPosition';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import selectors from '@/redux/selectors/selectors';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import { flattenObjectKeys } from '@shared/utils/flattenObjectKeys.js';
import { pluck } from '@shared/utils/pluck.js';
import { sliceText } from '@shared/utils/sliceText.js';
import { castArgumentDataType } from '@shared/workflow/castArgumentDataType.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const sampleTextMap = (key) => ({
  string: key,
  number: Math.floor(Math.random() * 1000000),
  boolean: true,
  date: new Date().toISOString()
});

const buildArgObject = (args) => {
  const result = {};

  if (!args) {
    return result;
  }

  args.forEach((n) => {
    const key = n?.key?.replaceAll('[]', '[0]');
    const dataType = n?.dataType || 'string';
    const itemDataType = n?.itemDataType;
    const isArrayOfObjects = dataType === 'array' && (!itemDataType || itemDataType === 'object');

    if (!['object'].includes(dataType) && !!n?.key && !isArrayOfObjects) {
      const value = n?.defaultValue || sampleTextMap(key)[dataType] || sampleTextMap(key).string;
      try {
        result[n?.key] = castArgumentDataType({ value, dataType, itemDataType });
      } catch (e) {
        result[n?.key] = value;
      }
    }
  });

  return result;
};

const autocomplete = (input = '', data, selectedIndex = 0) => {
  let suggestions = [];
  const parts = input?.trim()?.split('.') || [];
  let currentData = data;

  for (let i = 0; i < parts.length - 1; i++) {
    if (currentData[parts[i]] && typeof currentData[parts[i]] === 'object') {
      currentData = currentData[parts[i]];
    } else {
      return [];
    }
  }

  // If input does not end with '.', filter properties based on the last part
  const lastPart = parts[parts.length - 1] || '';
  if (typeof currentData === 'object' && currentData !== null) {
    suggestions = Object.keys(currentData)
      .filter((key) => key.startsWith(lastPart) && key !== lastPart)
      .map((key, idx) => {
        const value = currentData[key];
        const type = Array.isArray(value) ? 'array' : typeof value;
        const numberOfChildren = typeof value === 'object' ? Object.keys(value).length : 0;
        const hint = sliceText(type === 'object' ? `{ [${numberOfChildren}] }` : String(value || ''), 10);

        const start = lastPart;
        const end = key.substring(lastPart.length);

        return { label: key, labelStart: start, labelEnd: end, type, hint, selected: idx === selectedIndex };
      });
  }

  return suggestions;
};

const useSuggestions = ({ input, types, onChange }) => {
  const suggestionContexts = Array.isArray(types) ? types : [types];

  const [availableSuggestions, setAvailableSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lastKnownPosition, setLastKnownPosition] = useState(null);

  const activeActionId = useArgSelector(selectors.param, 'arg1');
  const responseInput = useAppScreenResponseInput({ settings: { activeActionId } });

  const integrationId = useArgSelector(selectors.param, 'integrationId');
  const integration = useArgSelector(selectors.embeddedIntegrationById, integrationId);
  const metadataDefinitions = integration?.settings?.metadataDefinitions;
  const credentialSecrets = useSelector(memoizeSelector('credentialSecrets', (state) => appScreenSelectors.activeAppDefaultTestCred(state)?.secrets) || {});
  const credentialCustom = useSelector(memoizeSelector('credentialCustom', (state) => appScreenSelectors.activeAppDefaultTestCred(state)?.secrets?.custom) || {});
  const apiArguments = useSelector(memoizeSelector('suggestions:apiArguments', (state) => appScreenSelectors.activeAppApi(state)?.arguments?.map((n) => pluck(n, ['location', 'key', 'defaultValue']))));
  const customArgs = useSelector(
    memoizeSelector(
      'suggestions:customArgs',
      (state) =>
        appScreenSelectors
          .activeApp(state)
          ?.authentication?.filter((n) => n.type === 'custom')
          ?.map((n) => n.arguments || [])
          ?.flat() || []
    )
  );

  const authArgs = useSelector(
    memoizeSelector(
      'suggestions:authArgs',
      (state) =>
        appScreenSelectors
          .activeApp(state)
          ?.authentication?.filter((n) => n.type !== 'custom')
          ?.map((n) => n.arguments || [])
          ?.flat() || []
    )
  );

  const pollingActionAttributes = useSelector(
    memoizeSelector('pollingActionAttributes', (state) => {
      const pollingActionId = appScreenSelectors.activeAppAction(state)?.longPolling?.actionId;
      return appScreenSelectors.activeAppActionById(state, pollingActionId)?.attributes;
    })
  );

  const actionArguments = useSelector(memoizeSelector('suggestions:actionArguments', (state) => appScreenSelectors.activeAppAction(state)?.arguments?.map((n) => pluck(n, ['id', 'key', 'dataType']))));
  const activeArgumentDependencies = useSelector(memoizeSelector('suggestions:activeArgumentDependencies', (state) => appScreenSelectors.activeAppActionArgument(state)?.dependencies));
  const dependencyArguments = activeArgumentDependencies?.map((id) => actionArguments?.find((a) => a.id === id));

  const customArgsObject = useMemo(() => buildArgObject(customArgs), [customArgs]);
  const authArgsObject = useMemo(() => buildArgObject(authArgs), [authArgs]);

  const activeSuggestion = availableSuggestions?.[selectedIndex];
  const registrationResponse = responseInput?.meta?.registrationResponse || {};

  const appsObject = useMemo(
    () => ({
      credentials: { ...authArgsObject, ...credentialSecrets, ...customArgsObject, ...credentialCustom },
      registrationResponse,
      system: { guid: '', uuid: '', base64encode: '' } // for display only purposes
    }),
    [authArgsObject, credentialSecrets, customArgsObject, credentialCustom, registrationResponse]
  );

  const triggerObject = useMemo(() => ({ arguments: Object.fromEntries(actionArguments?.map((n) => [n?.key, '']) || []), lastPollOn: 'date' }), [actionArguments]);

  const extraTriggerObject = useMemo(() => ({ pollingResult: Object.fromEntries(pollingActionAttributes?.map((n) => [n?.key, n?.dataType]) || []) }), [pollingActionAttributes]);

  const dependencyObject = useMemo(() => ({ dependencies: Object.fromEntries((dependencyArguments || []).map((a) => [a?.key, a?.dataType])) }), [dependencyArguments]);

  const installationLabelObject = useMemo(
    () => ({
      metadata: Object.fromEntries((metadataDefinitions || []).map((definition) => [definition?.key, ''])),
      endUser: { email: '', name: '' }
    }),
    [metadataDefinitions]
  );

  const suggestionObjectsMap = useMemo(
    () => ({
      [suggestions.apps]: flattenObjectKeys({ obj: appsObject }),
      [suggestions.longPollingTriggers]: flattenObjectKeys({ obj: { trigger: triggerObject } }),
      [suggestions.longPollingExtraAction]: flattenObjectKeys({ obj: { trigger: extraTriggerObject } }),
      [suggestions.argumentDependencies]: flattenObjectKeys({ obj: dependencyObject }),
      [suggestions.installationLabel]: flattenObjectKeys({ obj: installationLabelObject })
    }),
    [appsObject, triggerObject, extraTriggerObject, dependencyObject, installationLabelObject]
  );

  const apiParams = useMemo(() => Object.fromEntries(apiArguments?.filter((n) => n?.location === 'param').map((n) => [n?.key, n?.defaultValue || '']) || []), [apiArguments]);

  const actionParams = useMemo(() => Object.fromEntries(actionArguments?.filter((n) => n?.location === 'param').map((n) => [n?.key, n?.defaultValue || '']) || []), [actionArguments]);

  const paramObject = useMemo(
    () => ({
      ...apiParams,
      ...actionParams
    }),
    [apiParams, actionParams]
  );

  const updateSelectedSuggestion = (newIndex) => {
    setAvailableSuggestions((prevSuggestions) =>
      prevSuggestions.map((suggestion, index) => ({
        ...suggestion,
        selected: index === newIndex
      }))
    );
  };

  const buildSuggestions = useCallback(() => {
    const { delimiter, value } = extractSuggestionContent(input);

    if (typeof value === 'string') {
      let results = [];

      // Handle different delimiters and contexts
      if (delimiter === '/:') {
        // Param suggestions
        results = autocomplete(value, paramObject, selectedIndex);
      } else {
        // For {{ }} delimiters, use the appropriate suggestion contexts
        for (const context of suggestionContexts) {
          const contextObject = suggestionObjectsMap[context];
          if (contextObject) {
            const contextResults = autocomplete(value, contextObject, selectedIndex);
            results = [...results, ...contextResults];
          }
        }
      }

      setAvailableSuggestions(results);
      return;
    }

    setAvailableSuggestions([]);
  }, [input, paramObject, selectedIndex, suggestionContexts, suggestionObjectsMap]);

  const handleKeyDown = useCallback(
    (e) => {
      if (availableSuggestions.length && ['ArrowDown', 'ArrowUp', 'Tab', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [availableSuggestions.length]
  );

  const handleKeyUp = useCallback(
    (e) => {
      if (!input) {
        return;
      }
      // Track cursor position on key events
      setLastKnownPosition(input.selectionStart || getContentEditableCaretPosition(input));

      if (['ArrowDown', 'ArrowUp', 'Tab'].includes(e.key) && availableSuggestions.length) {
        const direction = e.key === 'ArrowDown' || e.key === 'Tab' ? -1 : 1;
        setSelectedIndex((prevIndex) => {
          const newIndex =
            direction === 1
              ? (prevIndex - 1 + availableSuggestions.length) % availableSuggestions.length || 0 //
              : (prevIndex + 1) % availableSuggestions.length || 0;
          updateSelectedSuggestion(newIndex);
          return newIndex;
        });
        return;
      }
      if (['Enter', ' '].includes(e.key) && availableSuggestions.length) {
        handleComplete();
        return;
      }

      if (['Escape'].includes(e.key) && availableSuggestions.length) {
        setAvailableSuggestions([]);
      }

      if (e.key !== 'Escape') {
        buildSuggestions();
      }

      setSelectedIndex(0);
    },
    [input, availableSuggestions.length, buildSuggestions]
  );

  const handleComplete = useCallback(
    (suggestion = activeSuggestion) => {
      const { delimiter } = extractSuggestionContent(input);
      const value = input?.value || input?.textContent;
      const selectionStart = lastKnownPosition ?? (input?.selectionStart || getContentEditableCaretPosition(input));

      if (!value || selectionStart === null || selectionStart === undefined) {
        return;
      }

      const startValue = value.slice(0, selectionStart);
      const endValue = value.slice(selectionStart);
      const suggestionLabel = suggestion?.labelEnd || '';

      if (delimiter === null) {
        const hasStartingDelimiter = startValue.endsWith('{{') || endValue.startsWith('{{');
        const hasClosingAhead = endValue.includes('}}');

        const newValue = hasStartingDelimiter ? `${startValue}${suggestionLabel}${hasClosingAhead ? '' : '}}'}${endValue}` : `${startValue}{{${suggestionLabel}}${hasClosingAhead ? '' : '}'}${endValue}`;

        if (input.value) {
          input.value = newValue;
        }
        if (typeof onChange === 'function') {
          onChange({ target: { value: newValue } });
        }
        buildSuggestions();
        return;
      }

      // delimiter is non-null here, so the opening `{{` is already present in startValue —
      // appending the suggestion completes the token; re-adding `{{` would double it.
      const endDelimiter = delimiter === '{{' ? '}}' : '';
      const hasClosingAhead = endValue.includes('}}');
      const newValue = `${startValue}${suggestionLabel}${hasClosingAhead ? '' : endDelimiter}${endValue}`;

      if (input.value) {
        input.value = newValue;
      }

      if (typeof onChange === 'function') {
        onChange({ target: { value: newValue } });
      }

      buildSuggestions();
    },
    [input, lastKnownPosition, activeSuggestion, onChange, buildSuggestions]
  );

  const handleMouseUp = useCallback(() => {
    setLastKnownPosition(input.selectionStart || getContentEditableCaretPosition(input));
  }, [input]);

  useEffect(() => {
    if (input) {
      input.addEventListener('keydown', handleKeyDown);
      input.addEventListener('keyup', handleKeyUp);
      input.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      if (input) {
        input.removeEventListener('keydown', handleKeyDown);
        input.removeEventListener('keyup', handleKeyUp);
        input.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, [input, handleKeyDown, handleKeyUp, handleMouseUp]);

  return { availableSuggestions, handleComplete };
};

export default useSuggestions;
